import { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { endOfDay, startOfDay } from 'date-fns';
import { CSVLink, Flex, StyledDatePicker, StyledFormSelect, WrappedQuery } from '../';
import {
  ALL_INSTITUTION_TYPES,
  ALL_INTERVENTIONS,
  ALL_SERVICE_PROVIDERS,
  EXPORT_SCHOOLS,
  INSTITUTIONS_FILTERED,
} from '../../../graphql';
import { formatSelectValue, formatInstitutionsProfileData, getPortal, sanitizeData } from '../../../utils';
import { institutionHeaders } from '../../../consts';
import { getAllProgrammesQuery } from '../../../containers/shared/programmes/utils';

type Props = {
  programme: string;
  serviceProvider: string;
  school: string;
  intervention: string;
  institutionType: string;
};

type State = {
  programmeIds?: string[];
  serviceProviderIds?: string[];
  interventionIds?: string[];
  schoolIds?: string[];
  institutionType?: { label: string; value: string };
};

const SchoolExport = ({ school, serviceProvider, programme, intervention, institutionType }: Props) => {
  const [state, setState] = useState<State>({});
  const [institutions, setInstitutions] = useState<[{ node: { id: string } }] | any>([]);
  const [portal] = useState(getPortal());
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const { data: programmeData, error: programmeError, loading: programmeDataLoading } = useQuery(
    getAllProgrammesQuery(portal),
    {
      variables: {
        input: {
          isArchived: false,
          includeWhereReporter: true,
        },
      },
    }
  );
  const { data: spData, error: spError, loading: spDataLoading } = useQuery(ALL_SERVICE_PROVIDERS, {
    variables: {
      input: {
        ...(state.programmeIds && { programmeIds: state.programmeIds }),
      },
    },
  });
  const { data: interventionData, error: interventionError, loading: interventionDataLoading } = useQuery(
    ALL_INTERVENTIONS,
    {
      variables: {
        input: {
          ...(state.programmeIds && { programmeIds: state.programmeIds }),
          ...(state.serviceProviderIds && { serviceProviderIds: state.serviceProviderIds }),
        },
      },
    }
  );
  const {
    data: institutionData,
    fetchMore: fetchMoreInstitutions,
    error: institutionError,
    loading: institutionLoading,
  } = useQuery(INSTITUTIONS_FILTERED, {
    variables: {
      input: {
        first: 10,
        ...(state.programmeIds && { programmeIds: state.programmeIds }),
        ...(state.interventionIds && { interventionIds: state.interventionIds }),
      },
    },
  });
  const {
    data: institutionTypeData,
    error: institutionTypeError,
    loading: institutionTypeLoading,
  } = useQuery(ALL_INSTITUTION_TYPES, { variables: { input: {} } });

  useEffect(() => {
    if (institutionData && institutionData.institutions && institutionData.institutions.edges) {
      setInstitutions(institutionData.institutions.edges);
    }
  }, [institutionData]);

  return (
    <Flex size="1" direction="column">
      <Flex size="1" direction="row" marginRight="10px">
        <StyledFormSelect
          isMulti={true}
          isClearable={true}
          name="programmeIds"
          label="Programme"
          placeholder={programmeDataLoading ? 'Data loading, Please wait' : 'Data ready, Please select option'}
          isDisabled={programmeDataLoading}
          onChange={(option: any[]) => {
            const newState = Object.assign({}, state, {
              [programme]: option ? option.map(opt => opt.value) : undefined,
            });
            setState(newState);
          }}
          options={
            programmeDataLoading || programmeError || !(programmeData && programmeData.programmes)
              ? []
              : programmeData.programmes.map((item: any) =>
                  formatSelectValue({
                    label: item.name,
                    value: item.id,
                  })
                )
          }
        />
      </Flex>
      <Flex size="1" direction="row" marginRight="10px">
        <StyledFormSelect
          isMulti={true}
          isClearable={true}
          name="serviceProviderIds"
          label="Service Provider"
          placeholder={spDataLoading ? 'Data loading, Please wait' : 'Data ready, Please select option'}
          isDisabled={spDataLoading}
          onChange={(option: any[]) => {
            const newState = Object.assign({}, state, {
              [serviceProvider]: option ? option.map(opt => opt.value) : undefined,
            });
            setState(newState);
          }}
          options={
            spDataLoading || spError || !(spData && spData.serviceProviders)
              ? []
              : spData.serviceProviders.map((item: any) =>
                  formatSelectValue({
                    label: item.name,
                    value: item.id,
                  })
                )
          }
        />
      </Flex>
      <Flex size="1" direction="row" marginRight="10px">
        <StyledFormSelect
          isMulti={true}
          isClearable={true}
          name="interventionIds"
          label="Intervention"
          placeholder={interventionDataLoading ? 'Data loading, Please wait' : 'Data ready, Please select option'}
          isDisabled={interventionDataLoading}
          onChange={(option: any[]) => {
            const newState = Object.assign({}, state, {
              [intervention]: option ? option.map(opt => opt.value) : undefined,
            });
            setState(newState);
          }}
          options={
            interventionDataLoading || interventionError || !(interventionData && interventionData.allInterventions)
              ? []
              : interventionData.allInterventions.map((item: any) =>
                  formatSelectValue({
                    label: item.name,
                    value: item.id,
                  })
                )
          }
        />
      </Flex>
      <Flex size="1" direction="row" marginRight="10px">
        <StyledFormSelect
          isMulti={true}
          isClearable={true}
          name="schoolIds"
          label="Institution"
          placeholder={institutionLoading ? 'Data loading, Please wait' : 'Data ready, Please select option'}
          isDisabled={institutionLoading}
          onChange={(option: any[]) => {
            if (!option) {
              setState({ [school]: undefined });
            } else {
              const newState = Object.assign({}, state, { [school]: option.map(opt => opt.value) });
              setState(newState);
            }
          }}
          options={
            institutionLoading || institutionError || !Boolean(institutions.length)
              ? []
              : institutions.map((item: any) =>
                  formatSelectValue({
                    label: item.node.name,
                    value: item.node.id,
                  })
                )
          }
          scrollHandler={async () => {
            // call the next page of the filteredInsitutions query
            return (
              institutionData.institutions &&
              institutionData.institutions.pageInfo &&
              institutionData.institutions.pageInfo.hasNextPage &&
              fetchMoreInstitutions({
                variables: {
                  input: {
                    first: 10,
                    skip: institutions.length,
                    ...(state.programmeIds && { programmeIds: state.programmeIds }),
                    ...(state.interventionIds && { interventionIds: state.interventionIds }),
                  },
                },
              }).then(({ data }: any) => {
                if (data && data.institutions && data.institutions.edges) {
                  const newInstitutions = [...institutions, ...data.institutions.edges];
                  setInstitutions(newInstitutions);
                }
              })
            );
          }}
        />
      </Flex>
      <Flex size="1" direction="row" marginRight="10px">
        <StyledFormSelect
          isClearable={true}
          name="institutionType"
          label="Institution Type"
          placeholder={institutionTypeLoading ? 'Data loading, Please wait' : 'Data ready, Please select option'}
          isDisabled={institutionTypeLoading}
          onChange={(option: any[]) => {
            const newState = Object.assign({}, state, { [institutionType]: option });
            setState(newState);
          }}
          options={
            institutionTypeLoading ||
            institutionTypeError ||
            !(institutionTypeData && institutionTypeData.institutionTypes)
              ? []
              : institutionTypeData.institutionTypes.map((item: any) =>
                  formatSelectValue({ label: item.name, value: item.id })
                )
          }
        />
      </Flex>
      <Flex size="1.2" direction="column">
        <StyledDatePicker
          label="Intervention Dates"
          name={`interventionDate`}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
        />
      </Flex>

      {state.institutionType ? (
        <WrappedQuery
          query={EXPORT_SCHOOLS}
          variables={{
            input: {
              //first: 10,
              ...(state.interventionIds && { interventionIds: state.interventionIds }),
              ...(state.serviceProviderIds && { serviceProviderIds: state.serviceProviderIds }),
              ...(state.programmeIds && { programmeIds: state.programmeIds }),
              ...(state.schoolIds && { schoolIds: state.schoolIds }),
              ...(startDate && { startDate: startOfDay(new Date(startDate)) }),
              ...(endDate && { endDate: endOfDay(new Date(endDate)) }),
              ...(state.institutionType && { institutionTypeId: state.institutionType.value }),
            },
          }}
          overrideStates={true}
        >
          {({ loading, error, data, fetchMore }) => {
            if (loading) {
              return <div>Preparing data for export...</div>;
            }

            if (error) {
              return <div>Something went wrong</div>;
            }

            // call the next page of the exportSchools query
            data.institutions &&
              data.institutions.pageInfo &&
              data.institutions.pageInfo.hasNextPage &&
              fetchMore({
                variables: {
                  input: {
                    //first: 10,
                    ...(state.interventionIds && { interventionIds: state.interventionIds }),
                    ...(state.serviceProviderIds && { serviceProviderIds: state.serviceProviderIds }),
                    ...(state.programmeIds && { programmeIds: state.programmeIds }),
                    ...(state.schoolIds && { schoolIds: state.schoolIds }),
                    ...(startDate && { startDate: startOfDay(new Date(startDate)) }),
                    ...(endDate && { endDate: endOfDay(new Date(endDate)) }),
                    ...(state.institutionType && { institutionTypeId: state.institutionType.value }),
                    after: data.institutions && data.institutions.pageInfo && data.institutions.pageInfo.endCursor,
                  },
                },
                updateQuery: (previousResult, { fetchMoreResult }) => {
                  const newInstitutions = fetchMoreResult.institutions.edges;
                  const previousInstitutions = previousResult.institutions ? previousResult.institutions.edges : [];
                  return {
                    institutions: {
                      aggregate: fetchMoreResult.institutions.aggregate,
                      pageInfo: fetchMoreResult.institutions.pageInfo,
                      edges: [...previousInstitutions, ...newInstitutions],
                      __typename: fetchMoreResult.institutions.__typename,
                    },
                  };
                },
              });

            if (data.institutions && data.institutions.pageInfo && data.institutions.pageInfo.hasNextPage) {
              return <div>Fetching more pages...</div>;
            }

            const formatted: any[] =
              data.institutions && data.institutions.edges
                ? formatInstitutionsProfileData({
                    data: data.institutions.edges,
                    filter: {
                      ...(state.interventionIds && { interventionIds: state.interventionIds }),
                      ...(state.serviceProviderIds && { serviceProviderIds: state.serviceProviderIds }),
                      ...(state.programmeIds && { programmeIds: state.programmeIds }),
                    },
                  })
                : [];

            const sanitized = formatted.length ? sanitizeData(formatted) : [];
            return (
              <Flex size="1" direction="row" marginRight="10px">
                {sanitized && sanitized.length > 0 && (
                  <CSVLink
                    filename={`InstitutionsSPExport-${new Date()}.csv`}
                    headers={institutionHeaders[(state.institutionType && state.institutionType.label) || 'School']}
                    data={sanitized}
                  >
                    Export CSV
                  </CSVLink>
                )}

                {sanitized && sanitized.length < 1 && <span>No institutions data for given parameters</span>}
              </Flex>
            );
          }}
        </WrappedQuery>
      ) : (
        <div>No Institution Type Selected</div>
      )}
    </Flex>
  );
};

export default SchoolExport;
